import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, X, Plus, Edit2, Check, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

const ParameterGradeMapping = () => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingOption, setEditingOption] = useState(null);
  const [bomParts, setBomParts] = useState({});

  // Sample BOM parts data
  const sampleBOMParts = {
    'Display': [
      { name: 'Display', quality: 'Original', cost: 4500, supplier: 'Supplier A' },
      { name: 'Display', quality: 'OLED', cost: 3800, supplier: 'Supplier B' },
      { name: 'Display', quality: 'Incell', cost: 2800, supplier: 'Supplier C' },
      { name: 'Display', quality: 'Copy', cost: 2000, supplier: 'Supplier D' }
    ],
    'Battery': [
      { name: 'Battery', quality: 'Original', cost: 2200, supplier: 'Supplier A' },
      { name: 'Battery', quality: 'Compatible', cost: 1500, supplier: 'Supplier E' }
    ],
    'Back Panel': [
      { name: 'Back Panel', quality: 'Original', cost: 2500, supplier: 'Supplier A' },
      { name: 'Back Panel', quality: 'OEM', cost: 1800, supplier: 'Supplier B' },
      { name: 'Back Panel', quality: 'Copy', cost: 1200, supplier: 'Supplier C' }
    ],
    'Complete Housing': [
      { name: 'Complete Housing', quality: 'Original', cost: 5500, supplier: 'Supplier A' },
      { name: 'Complete Housing', quality: 'OEM', cost: 4200, supplier: 'Supplier B' },
      { name: 'Complete Housing', quality: 'Copy', cost: 3200, supplier: 'Supplier C' }
    ],
    'Camera': [
      { name: 'Camera', quality: 'Original', cost: 3800, supplier: 'Supplier A' },
      { name: 'Camera', quality: 'OEM', cost: 2800, supplier: 'Supplier B' },
      { name: 'Camera', quality: 'Compatible', cost: 2200, supplier: 'Supplier C' }
    ]
  };

  useEffect(() => {
    loadTemplate();
    loadBOMParts();
  }, []);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/iqc/templates/Android`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplate(response.data);
    } catch (error) {
      console.error('Error loading template:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadBOMParts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/approver/bom/parts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Convert parts array to map
        const partsMap = {};
        response.data.parts.forEach(part => {
          partsMap[part.part_name] = part.quality_variants;
        });
        setBomParts(partsMap);
      }
    } catch (error) {
      console.error('Error loading BOM parts:', error);
      // Fallback to sample data
      setBomParts(sampleBOMParts);
    }
  };

  const OptionMappingRow = ({ category, parameter, option, optionIdx }) => {
    const isEditing = editingOption === `${parameter.parameter_id}_${optionIdx}`;
    const [localOption, setLocalOption] = useState(option);

    const gradeImpact = {
      excellent: localOption.grade_impact?.excellent || 0,
      superb: localOption.grade_impact?.superb || 0,
      good: localOption.grade_impact?.good || 0,
      fair: localOption.grade_impact?.fair || 0,
      okay: localOption.grade_impact?.okay || 0
    };

    const getGradeColor = (impact) => {
      if (impact === 0) return 'bg-green-100 text-green-800';
      if (impact === 1) return 'bg-red-100 text-red-800';
      return 'bg-yellow-100 text-yellow-800';
    };

    const getGradeLabel = (impact) => {
      if (impact === 0) return '✓ Pass';
      if (impact === 1) return '✗ Fail';
      return '⚠ Check';
    };

    return (
      <tr className="border-b hover:bg-gray-50">
        {/* Parameter Info */}
        <td className="px-4 py-3 text-xs">
          <div className="font-bold text-gray-900">{parameter.parameter_number}</div>
          <div className="text-gray-600 text-xs mt-1">{parameter.name}</div>
        </td>

        {/* Option Result */}
        <td className="px-4 py-3 text-xs">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-500">{option.option_number}</span>
            <span className="text-gray-900">{option.value}</span>
          </div>
        </td>

        {/* Engineer Profile */}
        <td className="px-4 py-3 text-xs">
          {option.triggers_engineer ? (
            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded-full text-xs font-bold">
              {option.triggers_engineer}
            </span>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </td>

        {/* Spare Parts */}
        <td className="px-4 py-3 text-xs">
          {option.triggers_parts && option.triggers_parts.length > 0 ? (
            <div className="space-y-1">
              {option.triggers_parts.map((part, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-blue-50 text-blue-800 rounded text-xs font-medium">
                    {part}
                  </span>
                  {bomParts[part] && (
                    <span className="text-gray-500 text-xs">
                      ({bomParts[part].length} options)
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <span className="text-gray-400">No parts</span>
          )}
        </td>

        {/* Grade Impact */}
        <td className="px-4 py-3">
          <div className="grid grid-cols-5 gap-1">
            <div className={`px-1 py-1 rounded text-center text-xs ${getGradeColor(gradeImpact.excellent)}`}>
              <div className="font-bold">E</div>
              <div className="text-xs">{getGradeLabel(gradeImpact.excellent)}</div>
            </div>
            <div className={`px-1 py-1 rounded text-center text-xs ${getGradeColor(gradeImpact.superb)}`}>
              <div className="font-bold">S</div>
              <div className="text-xs">{getGradeLabel(gradeImpact.superb)}</div>
            </div>
            <div className={`px-1 py-1 rounded text-center text-xs ${getGradeColor(gradeImpact.good)}`}>
              <div className="font-bold">G</div>
              <div className="text-xs">{getGradeLabel(gradeImpact.good)}</div>
            </div>
            <div className={`px-1 py-1 rounded text-center text-xs ${getGradeColor(gradeImpact.fair)}`}>
              <div className="font-bold">F</div>
              <div className="text-xs">{getGradeLabel(gradeImpact.fair)}</div>
            </div>
            <div className={`px-1 py-1 rounded text-center text-xs ${getGradeColor(gradeImpact.okay)}`}>
              <div className="font-bold">O</div>
              <div className="text-xs">{getGradeLabel(gradeImpact.okay)}</div>
            </div>
          </div>
        </td>

        {/* Actions */}
        <td className="px-4 py-3 text-right">
          <button
            onClick={() => setEditingOption(`${parameter.parameter_id}_${optionIdx}`)}
            className="text-blue-600 hover:text-blue-800 text-xs"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-4 mx-auto mb-4" style={{ borderColor: ZOBOX_BLUE }}></div>
          <div className="text-sm text-gray-600">Loading parameter mappings...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Parameter & Grade Mapping</h1>
              <p className="text-sm text-gray-600 mt-1">Configure QC parameters, spare parts, and grade impact</p>
            </div>
            <div className="flex items-center gap-3">
              <button className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                <Save className="w-4 h-4" />
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-bold text-blue-900 mb-2">Grade Impact Legend</h3>
          <div className="flex items-center gap-6 text-xs">
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded">✓ Pass (0)</span>
              <span className="text-gray-600">- No impact on grade</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-1 bg-red-100 text-red-800 rounded">✗ Fail (1)</span>
              <span className="text-gray-600">- Blocks this grade</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="font-bold">E</span>: Excellent |
              <span className="font-bold">S</span>: Superb |
              <span className="font-bold">G</span>: Good |
              <span className="font-bold">F</span>: Fair |
              <span className="font-bold">O</span>: Okay
            </div>
          </div>
        </div>
      </div>

      {/* Parameter Tables by Category */}
      <div className="max-w-7xl mx-auto px-6 pb-8">
        {template?.categories?.map((category) => (
          <div key={category.category_id} className="mb-6">
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              {/* Category Header */}
              <div className="px-6 py-4 border-b" style={{ backgroundColor: ZOBOX_BLUE }}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{category.icon}</span>
                  <div>
                    <h2 className="text-lg font-bold text-white">
                      Category {category.category_number}: {category.name}
                    </h2>
                    <p className="text-sm text-blue-100">
                      {category.parameters.length} parameters
                    </p>
                  </div>
                </div>
              </div>

              {/* Parameters Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Parameter</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Result / Option</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Engineer Profile</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Spare Parts</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Grade Impact (E/S/G/F/O)</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {category.parameters.map((parameter) => (
                      parameter.options.map((option, optionIdx) => (
                        <OptionMappingRow
                          key={`${parameter.parameter_id}_${optionIdx}`}
                          category={category}
                          parameter={parameter}
                          option={option}
                          optionIdx={optionIdx}
                        />
                      ))
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ParameterGradeMapping;
